import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    createBlog,
    updateBlogAction,
    fetchBlogDetails
} from "../../../../utils/redux/blog/blogActions";

import { fetchCategories } from "../../../../utils/redux/blogCategory/blogCategoryActions";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { uploadToCloudinary } from "../../../../helpers/upload";
import RichTextEditor from "../../../Reusables/RichTextEditor/RichTextEditor";

const CreateBlogPage = () => {
    const { id } = useParams();
    const isEdit = Boolean(id);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { selectedBlog } = useSelector(state => state.blog);
    const { categories } = useSelector(state => state.blogCategory);

    const editorRef = useRef(null);

    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState({
        title: "",
        description: "",
        content: "",
        author: "",
        category: "",
        image: { url: "" }
    });

    const [imageFile, setImageFile] = useState(null);

    /* ================= FETCH ================= */

    useEffect(() => {
        dispatch(fetchCategories());
        if (isEdit) dispatch(fetchBlogDetails(id));
    }, []);

    useEffect(() => {
        if (selectedBlog) {
            setForm(selectedBlog);
            if (editorRef.current) {
                editorRef.current.innerHTML = selectedBlog.content || "";
            }
        }
    }, [selectedBlog]);

    /* ================= EDITOR ================= */

    const handleEditorChange = () => {
        setForm(prev => ({
            ...prev,
            content: editorRef.current.innerHTML
        }));
    };

    const formatText = (command) => {
        document.execCommand(command, false, null);
    };

    /* ================= IMAGE ================= */

    const handleImageUpload = async () => {
        if (!imageFile) return form.image;

        const res = await uploadToCloudinary(imageFile, {
            module: "blog",
            type: "image",
            slug: form.title
        });

        return {
            url: res.secure_url,
            public_id: res.public_id
        };
    };

    /* ================= SAVE ================= */

    const handleSave = async () => {
        if (!form.title || !form.category) {
            toast.error("Title & Category required");
            return;
        }

        setLoading(true);

        try {
            const uploadedImage = await handleImageUpload();

            const payload = {
                ...form,
                image: uploadedImage
            };

            const res = isEdit
                ? await dispatch(updateBlogAction(id, payload))
                : await dispatch(createBlog(payload));

            if (res?.success) {
                toast.success("Blog saved");
                navigate("/blogs");
            } else {
                toast.error("Save failed");
            }

        } catch (err) {
            toast.error("Error saving blog");
        } finally {
            setLoading(false);
        }
    };

    /* ================= UI ================= */

    return (
        <div className="container py-4">
            <div className="card">
                <div className="card-body">

                    <h4 className="mb-4">
                        {isEdit ? "Edit Blog" : "Create Blog"}
                    </h4>

                    <div className="row">

                        {/* TITLE */}
                        <div className="col-md-6 mb-3">
                            <label>Title *</label>
                            <input
                                className="form-control"
                                value={form.title}
                                onChange={e =>
                                    setForm({ ...form, title: e.target.value })
                                }
                            />
                        </div>

                        {/* CATEGORY */}
                        <div className="col-md-6 mb-3">
                            <label>Category *</label>
                            <select
                                className="form-select"
                                value={form.category}
                                onChange={e =>
                                    setForm({ ...form, category: e.target.value })
                                }
                            >
                                <option value="">Select</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* AUTHOR */}
                        <div className="col-md-6 mb-3">
                            <label>Author</label>
                            <input
                                className="form-control"
                                value={form.author}
                                onChange={e =>
                                    setForm({ ...form, author: e.target.value })
                                }
                            />
                        </div>

                        {/* DESCRIPTION */}
                        <div className="col-12 mb-3">
                            <label>Description</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                value={form.description}
                                onChange={e =>
                                    setForm({ ...form, description: e.target.value })
                                }
                            />
                        </div>

                        {/* IMAGE */}
                        <div className="col-md-6 mb-3">
                            <label>Upload Image</label>
                            <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={e => setImageFile(e.target.files[0])}
                            />
                        </div>

                        {/* PREVIEW */}
                        {form.image?.url && (
                            <div className="col-md-6 mb-3">
                                <img
                                    src={form.image.url}
                                    alt="preview"
                                    style={{ width: "120px" }}
                                />
                            </div>
                        )}
                    </div>

                    {/* ================= RICH TEXT EDITOR ================= */}

                    <h5 className="mt-4 mb-2">Content</h5>

                    {/* TOOLBAR */}
                    <RichTextEditor
                        value={form.content}
                        onChange={(val) =>
                            setForm(prev => ({ ...prev, content: val }))
                        }
                    />

                    {/* SAVE */}
                    <div className="mt-4 text-end">
                        <button
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={loading}
                        >
                            {loading ? "Saving..." : "Save Blog"}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default CreateBlogPage;